
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { isEqual } from 'lodash';
import { OPTION } from '../../utils/constants';
import { formatOptionGroup } from '../../utils/format';
import Toast from '@/utils/toast';
import TitleHeader from '@/components/shared/EditorHeader.vue';
import ListItem from '@/components/shared/ListItem.vue';
import AddonEditor from '@/components/add-ons/AddOnEditor.vue';
import AddonsLocaleEditor from './AddonsLocaleEditor.vue';
import RichTextEditor from '@/components/shared/RichTextEditor.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'options';

@Component<AddOnOptionGroupEditor>({
	components: {
		TitleHeader,
		ListItem,
		AddonsLocaleEditor,
		AddonEditor,
		RichTextEditor,
		TextField
	}
})

export default class AddOnOptionGroupEditor extends Vue {
	@Action('createOptionGroup', { namespace }) private createOptionGroupAction!: (args: object) => Promise<number | undefined>;
	@Action('updateOptionGroupAndOptions', { namespace }) private updateOptionGroupAndOptionsAction!: (args: object) => Promise<void>;
	@Getter('getSelectedAddOnOptionGroup', { namespace }) private createdAddOnOptionGroup!: OptionGroup;
	@Prop({ type: Object, default: () => {} }) private optionGroup!: OptionGroup;
	@Prop({ type: Object, default: () => {} }) private selectedLocale!: RestaurantLocale;
	@Prop({ type: Boolean, default: true }) private isCreating!: boolean;
	@Prop({ type: Boolean, default: false }) private disabled!: boolean;

	private mutableOptionGroup: OptionGroup = this.optionGroup;
	private addOns: Option[] = this.optionGroup.values as Option[];
	private loading: boolean = false;

	private mutableAddOns: Option[] = this.addOns?.length
		? [...JSON.parse(JSON.stringify(this.addOns)), { ...OPTION, order: this.addOns.length - 1 }]
		: [{ ...OPTION, order: 0 }];

	private addOnsBatch: Batch = {
		removed: [],
		updated: this.addOns || [],
		added: this.addOns ? [] : [{ ...OPTION }]
	};

	/**
	 * Handle updated add-ons from the child component
	 *
	 * @param {Option[]} updatedAddOns
	 */
	private handleAddonsUpdated(updatedAddOns: Option[]): void {
		this.mutableAddOns = updatedAddOns;
		this.$emit('set-add-ons-edited', this.mutableAddOns);
	}

	/**
	 * Add the removed add-on id to the removed array and update the add-ons list
	 */
	private handleAddonRemoved({ updatedAddons, removedAddonId }: { updatedAddons: Option[]; removedAddonId: number }): void {
		this.addOnsBatch.removed.push(removedAddonId);
		this.handleAddonsUpdated(updatedAddons);
	}

	/**
	 * Update option group and options
	 *
	 * @param {Option[]} completedAddOns
	 * @return {Promise<void>}
	 */
	private updateAddOnOption(completedAddOns: Option[]) {
		// Remove the addOns that do not have an id, those are to be *added*, as opposed to *updated*
		this.addOnsBatch.updated = completedAddOns.filter((updatedAddOn: Option) => updatedAddOn.id);

		// Check which add-ons were actually updated. The ones that are unchanged will be removed.
		if (this.addOnsBatch.updated.length) {
			const existingAddOnsById: any = this.addOns.reduce((addOnByIdObj, current: Option) => {
				return {
					...addOnByIdObj,
					[current.id!]: current
				};
			}, {});

			this.addOnsBatch.updated = this.addOnsBatch.updated.filter((addOn: Option) => {
				const existing = existingAddOnsById[addOn.id!];
				return !isEqual(addOn, existing);
			});
		}

		// The ones that don't have an id are to be added:
		this.addOnsBatch.added = completedAddOns.filter(addOn => !addOn.id);

		return this.updateOptionGroupAndOptionsAction({
			id: this.optionGroup.id,
			optionsBatch: this.addOnsBatch,
			body: formatOptionGroup(this.mutableOptionGroup)
		});
	}

	/**
	 * Save the option group with the options (addons)
	 * We filter out any addons that are incomplete
	 *
	 * @return {Promise<void>}
	 */
	private async save(): Promise<void> {
		this.loading = true;
		const completedAddOns: Option[] = this.mutableAddOns.filter((addOn: Option) => addOn.price && addOn.name);

		if (this.isCreating) {
			await this.createOptionGroupAction({ options: completedAddOns, body: formatOptionGroup(this.mutableOptionGroup) })
				.then((createdOptionGroupId: number | undefined) => {
					// Automatically select a created add on option group
					if (this.$listeners.select) {
						this.$emit('select', createdOptionGroupId);
					}
				})
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage) => {
					Toast.error(this.$t('shared.error_creating', { name: this.mutableOptionGroup.name, errorMessage }));
				});
		}
		else {
			await this.updateAddOnOption(completedAddOns)
				.then(() => {
					this.$emit('close-modal', true);
				})
				.catch((errorMessage) => {
					Toast.error(this.$t('shared.error_updating', { name: this.mutableOptionGroup.name, errorMessage }));
				});
		}
		this.loading = false;
	}
}
